import { Clock, ExternalLink, FileText, Languages, Youtube } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type { YoutubeCaptionsResult } from 'sprites-types/workflow/tools/youtubeCaptions'
import { youtubeCaptions } from 'sprites-types/workflow/tools/youtubeCaptions'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function YoutubeCaptionsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(youtubeCaptions, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data
  const items: YoutubeCaptionsResult[] = Array.isArray(parsed) ? parsed : [parsed]
  const item = items[0]
  const inputs = nodeExecution.inputs

  const videoUrl =
    item?.url || (inputs.videoId ? `https://youtube.com/watch?v=${inputs.videoId}` : '')
  const videoTitle = inputs.title || 'YouTube Video'
  const captions = item?.captions || ''
  const language = inputs.language || 'en'
  const duration = inputs.duration

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Check if captions are timestamped
  const hasTimestamps =
    Array.isArray(item?.timestamped_captions) && item.timestamped_captions.length > 0

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-red-100 p-2">
              <Youtube className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4 text-red-600" />
              <div className="text-sm font-semibold text-gray-900">YouTube Captions</div>
            </div>

            <div className="mb-2 text-sm font-medium text-gray-900">{videoTitle}</div>

            <div className="mb-3 flex items-center gap-4 text-xs text-gray-600">
              {language && (
                <div className="flex items-center gap-1">
                  <Languages className="h-3 w-3" />
                  <span>{language.toUpperCase()}</span>
                </div>
              )}
              {duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{typeof duration === 'number' ? formatDuration(duration) : duration}</span>
                </div>
              )}
              {hasTimestamps && (
                <div className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700">
                  Timestamped
                </div>
              )}
            </div>

            <div className="mb-3 max-h-80 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-3">
              {hasTimestamps ? (
                <div className="space-y-1 font-mono text-xs text-gray-800">
                  {item?.timestamped_captions?.map((c) => (
                    <div key={`${c.startMs}-${c.endMs}`}>
                      [{c.startTimeText}] {c.text}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="whitespace-pre-wrap font-mono text-xs text-gray-800">
                  {captions}
                </div>
              )}
            </div>

            {videoUrl && (
              <div className="flex items-center gap-3">
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Watch Video
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadYoutubeCaptions(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function youtubeCaptionsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(youtubeCaptions, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return ''
  }

  const parsed = parseResult.data
  const items: YoutubeCaptionsResult[] = Array.isArray(parsed) ? parsed : [parsed]
  const inputs = nodeExecution.inputs

  const item = items[0]
  const videoUrl =
    item?.url || (inputs.videoId ? `https://youtube.com/watch?v=${inputs.videoId}` : '')
  const videoTitle = inputs.title || 'YouTube Video'
  const captions = item?.captions || ''
  const language = inputs.language || 'en'

  return `## YouTube Captions

**Video:** ${videoTitle}
${language ? `**Language:** ${language.toUpperCase()}` : ''}
${videoUrl ? `**URL:** ${videoUrl}` : ''}

**Captions:**
\n\n\`\`\`
${captions}
\`\`\`
\n---
`
}

export const YoutubeCaptionsDisplay = createWorkflowToolComponent(
  YoutubeCaptionsDisplayComponent,
  downloadYoutubeCaptions,
  youtubeCaptionsToMarkdown
)
